'use client';

import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { hasTypographyRole, withoutTokens } from '../typography-role';

/**
 * `sm`   = 30px compact (dense rows, table cells)
 * `md`   = 34px default field height (--h-field); the canonical size for all standard forms
 * `lg`   = 40px spacious (not commonly needed; prefer md)
 * `auth` = 46px tall variant for login/authentication screens
 * `hero` = unsized; the caller supplies scale and padding (display amounts)
 *
 * Approach: `md` is remapped to --h-field (34px) — the closest to all existing
 * call-sites which passed nothing or `md`. `lg` kept at h-10 (40px) to preserve
 * any call-site that explicitly wants the old md height. `auth` added at 46px.
 */
export type InputSize = 'sm' | 'md' | 'lg' | 'auth' | 'hero';
export type InputVariant = 'default' | 'seamless' | 'readOnly';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: InputSize;
  /**
   * Visual chrome. `default` is the bordered field; `seamless` is a borderless,
   * transparent-background input that fills its container with an inset focus
   * ring — for editable table/grid cells where a bordered box would inflate row
   * height past the density spec. Prefix/suffix fields honor this variant too.
   * `readOnly` is a sunken, non-interactive display of an immutable field value
   * — native `readOnly` (not `disabled`), focusable, selectable, with a lock glyph.
   */
  variant?: InputVariant;
  invalid?: boolean;
  /**
   * Softer, non-blocking signal than `invalid` — e.g. a plausibility check
   * that doesn't reject the value outright. Renders a warning-tinted border
   * instead of the danger one. `invalid` wins if both are set. Ignored on
   * `variant="seamless"` (no border chrome to tint).
   */
  warning?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /**
   * For type="password" inputs only: renders a show/hide toggle button as
   * the suffix, so the user can check what they typed instead of guessing
   * from a row of dots. Ignored for every other input type. Takes over the
   * suffix slot — do not also pass `suffix` alongside `revealable`.
   */
  revealable?: boolean;
  className?: string;
}

const SIZE: Record<InputSize, string> = {
  sm: 'h-[1.875rem] px-[var(--field-pad)] text-body-sm', // 30px compact
  md: 'h-[var(--h-field)] px-[var(--field-pad)] text-body-sm', // 34px default
  lg: 'h-10 px-[var(--field-pad)] text-body', // 40px spacious
  auth: 'h-[2.875rem] px-[var(--field-pad)] text-body', // 46px login/auth screens
  // Display-scale: contributes NO height, NO padding and NO text size, so the
  // caller owns all three. Every other size pins `h-*` and a `text-*`, which
  // is correct for a field and fatal for a hero -- CurrencyInput's 52px
  // amount was being rendered into a 34px-tall box with `text-body-sm`
  // competing against the caller's `text-metric-hero` at equal specificity,
  // so the amount silently came out field-sized. Only consumer today is
  // CurrencyInput `presentation="hero"`.
  hero: '',
};

// Brand focus ring: 3px ring at 20% opacity over brand-primary border.
// :-webkit-autofill override: forces token surface/text so autofill does not
// paint its own background color, preserving dark-mode token fidelity.
//
// border-color is deliberately NOT baked in here -- see TONE_BORDER below.
// A plain string-concat className (this codebase's established pattern,
// packages/backoffice-ui's twMerge-backed `cn` is a different package and
// not a dependency of this one) can't guarantee a caller-supplied override
// like `border-warning` beats a hardcoded `border-border` at equal CSS
// specificity: the winner is whichever utility Tailwind happens to emit
// LATER in the compiled stylesheet, which is a build-order artifact, not
// something the className string's own left-to-right order controls. Both
// branches below select exactly ONE border-color utility per render instead
// of ever letting two coexist.
const BASE =
  'w-full rounded-control border bg-surface text-primary ' +
  'placeholder:text-muted ' +
  'focus-visible:outline-none focus-visible:border-ring-focus ' +
  'focus-visible:ring-[3px] focus-visible:ring-ring-focus/20 ' +
  '[&:-webkit-autofill]:bg-surface [&:-webkit-autofill]:text-primary ' +
  'disabled:opacity-disabled disabled:cursor-not-allowed';

// Exclusive per-render border-color pick (never combined with `border-border`
// or with each other) -- see the BASE comment above for why this must be
// exclusive rather than an appended override.
const TONE_BORDER = {
  default: 'border-border',
  warning: 'border-warning',
  danger: 'border-danger',
} as const;
type InputTone = keyof typeof TONE_BORDER;

const INVALID_RING = 'focus-visible:ring-danger/20 focus-visible:border-danger';

// Borderless variant for editable table/grid cells: no border or bg chrome, with
// an INSET focus ring so focus never expands the row past the density floor.
const SEAMLESS =
  'w-full rounded-control border-0 bg-transparent text-primary placeholder:text-muted ' +
  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring-focus/20 focus-visible:ring-inset ' +
  'disabled:opacity-disabled disabled:cursor-not-allowed';
const SEAMLESS_INVALID = 'ring-[3px] ring-danger/20 ring-inset';

// The affix-wrapper branch below (prefix/suffix present) previously ignored
// `variant`/`size` entirely -- always bordered chrome at a fixed `py-2`,
// regardless of what was passed. That was invisible until a caller first
// tried `variant="seamless" size="sm"` on a prefixed/suffixed field (a dense
// table cell showing a `$`/`%` affix): the wrapper stayed bordered and
// full-height, silently ignoring both props. These two maps let the affix
// branch honor them while preserving the default field's existing padding
// and resulting height.
const AFFIX_HEIGHT: Record<InputSize, string> = {
  sm: 'h-[1.875rem]',
  md: '',
  lg: 'h-10',
  auth: 'h-[2.875rem]',
  hero: '',
};
// The size tiers' own text roles, dropped when the caller passes a role.
const OWN_TYPOGRAPHY: ReadonlySet<string> = new Set(['text-body', 'text-body-sm']);

const AFFIX_INPUT_SIZE: Record<InputSize, string> = {
  sm: 'py-1 text-body-sm',
  md: 'py-2 text-body-sm',
  lg: 'py-2.5 text-body',
  auth: 'py-3 text-body',
  hero: '',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    variant = 'default',
    invalid = false,
    warning = false,
    prefix,
    suffix,
    revealable = false,
    type,
    disabled,
    readOnly,
    className,
    ...rest
  },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const seamless = variant === 'seamless';
  const isReadOnly = variant === 'readOnly';
  // Note: readOnly inputs always render through the affix-wrapper branch below
  // (never the no-affix early-return) because they always include a lock icon.
  // The base/classes computation here only applies to the no-affix branch, which
  // is unreachable for readOnly, so we exclude it from the variant selection.
  const base = seamless ? SEAMLESS : BASE;
  // invalid wins over warning -- see the `warning` prop doc on InputProps.
  const tone: InputTone = invalid ? 'danger' : warning ? 'warning' : 'default';
  const seamlessToneCls = seamless && invalid ? SEAMLESS_INVALID : '';
  const borderedToneCls = seamless
    ? ''
    : tone === 'danger'
      ? `${TONE_BORDER.danger} ${INVALID_RING}`
      : TONE_BORDER[tone];
  // A caller typography role (e.g. `text-heading-2` on an amount field, #13460)
  // replaces the size tier's own `text-body(-sm)`; height and padding are kept.
  // Both roles on one element would resolve by stylesheet order, not className.
  const callerRole = hasTypographyRole(className);
  const sizeCls = callerRole ? withoutTokens(SIZE[size], OWN_TYPOGRAPHY) : SIZE[size];
  const affixInputSizeCls = callerRole
    ? withoutTokens(AFFIX_INPUT_SIZE[size], OWN_TYPOGRAPHY)
    : AFFIX_INPUT_SIZE[size];
  const classes = [base, sizeCls, seamlessToneCls, borderedToneCls, className]
    .filter(Boolean)
    .join(' ');

  const isRevealable = revealable && type === 'password';
  const effectiveType = isRevealable ? (visible ? 'text' : 'password') : type;
  const revealToggle = isRevealable && (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setVisible((v) => !v)}
      aria-label={visible ? 'Hide password' : 'Show password'}
      className="flex items-center text-muted hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus rounded-sm disabled:opacity-disabled disabled:cursor-not-allowed"
    >
      {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
  const lockIcon = isReadOnly && <Lock className="h-4 w-4" />;
  const effectiveSuffix = lockIcon || revealToggle || suffix;

  if (!prefix && !effectiveSuffix)
    return (
      <input
        ref={ref}
        type={effectiveType}
        disabled={disabled}
        readOnly={isReadOnly || readOnly}
        aria-invalid={invalid || undefined}
        className={classes}
        {...rest}
      />
    );
  return (
    <div
      className={[
        // `w-full` and `min-w-0` solve opposite halves of the same sizing
        // problem, and this wrapper needs both.
        //
        // `w-full` matches BASE's own `w-full` on the no-affix branch above --
        // without it this wrapper <div> shrink-wraps to its content (the
        // browser's default ~20ch input width), silently ignoring whatever
        // width its parent/column intends. A dense-table caller sizing its
        // column to e.g. 96px got a ~200px-wide field instead, overflowing
        // sibling columns off the visible edge.
        //
        // `min-w-0` stops the opposite failure: it REFUSING to shrink below
        // that same default. As a grid/flex item the wrapper's automatic
        // minimum is min-content -- computed from the inner input's intrinsic
        // ~20ch even though that input carries `min-w-0` itself -- so in a
        // narrower column the bordered wrapper overflowed instead of fitting.
        // Measured against the deployed stylesheet: 181px rendered inside a
        // 141px grid track, spilling 40px past the dialog's edge (the
        // quick-add dialog's Case pack / Cost / Sell price row).
        'min-w-0 w-full flex items-center gap-2 rounded-control focus-within:ring-[3px] focus-within:ring-ring-focus/20',
        seamless
          ? 'border-0 bg-transparent focus-within:ring-inset'
          : isReadOnly
            ? 'border border-border bg-surface-sunken focus-within:border-ring-focus'
            : `border ${TONE_BORDER[tone]} bg-surface focus-within:border-ring-focus`,
        AFFIX_HEIGHT[size],
        // The plain (no-affix) branch above gets its disabled treatment from
        // BASE's `disabled:opacity-disabled` -- a `disabled:` pseudo-class on
        // the <input> itself. This wrapper <div> can't be html-disabled, so
        // without this the affix branch silently dropped the muted-look cue
        // entirely: the field was still non-interactive (native `disabled`
        // on the inner input), but looked identical to an editable one.
        disabled
          ? seamless
            ? 'opacity-disabled cursor-not-allowed'
            : 'opacity-disabled cursor-not-allowed bg-surface-sunken'
          : isReadOnly
            ? 'cursor-default'
            : '',
        // The caller's `className` used to be dropped entirely on this
        // branch -- also applied on the wrapper (not just the input below)
        // because a WIDTH utility (e.g. `w-20`) can never take effect on the
        // input: it already carries `flex-1` (`flex-basis: 0%`), and per the
        // flexbox spec an explicit flex-basis beats `width` outright, no
        // matter what order the classes compile in. The wrapper is the only
        // element a width class can actually constrain.
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {prefix && <span className="pl-3 text-muted">{prefix}</span>}
      <input
        ref={ref}
        type={effectiveType}
        disabled={disabled}
        readOnly={isReadOnly || readOnly}
        aria-invalid={invalid || undefined}
        // Also applied here (redundant with the wrapper for width utilities,
        // which are inert on this flex-1 element -- see the wrapper comment
        // above) so that classes that DO target the input itself, like
        // `text-right` or `tabular-nums`, still land on the element that
        // actually renders the text.
        className={[
          // `min-w-0` is the actual fix for a second bug this branch had: a
          // flex item's default `min-width` is `auto` (its content's
          // intrinsic size), not 0 -- so even with the wrapper correctly
          // sized to its column/container, this `flex-1` input refused to
          // shrink below the browser's unconstrained default text-input
          // width (~170-200px) and visually overflowed its own wrapper.
          // `w-full` above fixed the wrapper's OWN size; this fixes what
          // its flex child is actually allowed to shrink to.
          `min-w-0 flex-1 bg-transparent border-0 outline-none px-[var(--field-pad)] ${affixInputSizeCls} disabled:cursor-not-allowed`,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
      {effectiveSuffix && <span className="pr-3 text-muted">{effectiveSuffix}</span>}
    </div>
  );
});
